// src/context/AppContext.js
import React, { createContext, useContext, useReducer } from "react";

// Initial state
const initialState = {
  data: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  searchTerm: "",
  notification: null,
  sidebarOpen: false, // For mobile responsiveness
};

// Action types
const ActionTypes = {
  FETCH_DATA_START: "FETCH_DATA_START",
  FETCH_DATA_SUCCESS: "FETCH_DATA_SUCCESS",
  FETCH_DATA_ERROR: "FETCH_DATA_ERROR",
  SET_PAGE: "SET_PAGE",
  SET_SEARCH_TERM: "SET_SEARCH_TERM",
  SET_NOTIFICATION: "SET_NOTIFICATION",
  CLEAR_NOTIFICATION: "CLEAR_NOTIFICATION",
  TOGGLE_SIDEBAR: "TOGGLE_SIDEBAR",
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.FETCH_DATA_START:
      return { ...state, loading: true, error: null };
    case ActionTypes.FETCH_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
        data: action.payload.items,
        totalPages: action.payload.totalPages,
      };
    case ActionTypes.FETCH_DATA_ERROR:
      return { ...state, loading: false, error: action.payload };
    case ActionTypes.SET_PAGE:
      return { ...state, page: action.payload };
    case ActionTypes.SET_SEARCH_TERM:
      return { ...state, searchTerm: action.payload, page: 1 };
    case ActionTypes.SET_NOTIFICATION:
      return { ...state, notification: action.payload };
    case ActionTypes.CLEAR_NOTIFICATION:
      return { ...state, notification: null };
    case ActionTypes.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };
    default:
      return state;
  }
}

// Create context
const AppContext = createContext();

// Context provider
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Actions
  const actions = {
    fetchDataStart: () => dispatch({ type: ActionTypes.FETCH_DATA_START }),
    fetchDataSuccess: (data) =>
      dispatch({ type: ActionTypes.FETCH_DATA_SUCCESS, payload: data }),
    fetchDataError: (error) =>
      dispatch({ type: ActionTypes.FETCH_DATA_ERROR, payload: error }),
    setPage: (page) => dispatch({ type: ActionTypes.SET_PAGE, payload: page }),
    setSearchTerm: (term) =>
      dispatch({ type: ActionTypes.SET_SEARCH_TERM, payload: term }),
    setNotification: (message, type) => {
      dispatch({
        type: ActionTypes.SET_NOTIFICATION,
        payload: { message, type },
      });
      // Auto clear after 5 seconds
      setTimeout(() => {
        dispatch({ type: ActionTypes.CLEAR_NOTIFICATION });
      }, 5000);
    },
    toggleSidebar: () => dispatch({ type: ActionTypes.TOGGLE_SIDEBAR }),
  };

  return (
    <AppContext.Provider value={{ state, actions }}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
